import { Component, OnInit, Input, DestroyRef } from '@angular/core';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { ActionsInterface } from './models/actions.interface';
import { LanguageService } from '@app/core/language/language.service';
import { TableOptionsInterface } from './models/options.interface';
import { ColumnsInterface } from './models/columns.interface';
import { TableUrlInterface } from './models/table-url.interface';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged, take, takeUntil } from 'rxjs/operators';
import { TableCoreService } from './services/table-core.service';
import { ListComponent } from '@app/shared/list/list.component';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.sass'],
  standalone: true,
  imports: [CommonModule, RouterModule, ReactiveFormsModule, TranslateModule, ListComponent]
})
export class TableComponent implements OnInit {
  @Input() data: any[];
  @Input() url: TableUrlInterface;
  @Input() columns: ColumnsInterface[];
  @Input() actions: ActionsInterface[];
  @Input() selectedData: any[];
  @Input() id = 'id';
  @Input() options: TableOptionsInterface;

  apis: any;
  globalSearch: FormGroup;
  isCollapsed: boolean;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    public lang: LanguageService,
    private fb: FormBuilder,
    private tableCore: TableCoreService,
    private destroyRef: DestroyRef
  ) {
    this.isCollapsed = true;
  }

  ngOnInit() {
    this.buildForm();
    this.searchInput();

    this.options = {
      add: true,
      search: true,
      reorder: false,
      loading: false,
      details: true,
      check: true,
      columnSearch: true,
      globalSearch: false,
      ...this.options
    };
  }

  deleteSelected() {
    const selectedIds: number[] = this.selectedData.map(item => item[this.id]);

    this.tableCore
      .deleteSelectedItems(this.url.deleteAll, selectedIds)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.getTableData();
        this.selectedData = [];
        this.isCollapsed = true;
      });
  }

  /**
   * Handle the global search input and update the table data
   * @memberof TableComponent
   */
  searchInput(): void {
    this.globalSearch.controls.search.valueChanges
      .pipe(debounceTime(700), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((resp: any) => {
        this.tableCore.search = resp;
        this.getTableData();
      });
  }

  /**
   * Get the Table Data from the Service
   * @memberof TableComponent
   */
  getTableData(): void {
    if (this.url && this.url.getAll) {
      this.tableCore
        .getAllData(this.url.getAll)
        .pipe(take(1), takeUntilDestroyed(this.destroyRef))
        .subscribe(() => (this.data = this.tableCore.tableData));
    }
  }

  /**
   * Get the selected array from the list child component
   * @param {any[]} selected
   * @memberof TableComponent
   */
  getSelected(selected: any[]): void {
    if (selected.length) {
      this.isCollapsed = false;
    } else {
      this.isCollapsed = true;
    }
    this.selectedData = selected;
  }

  /**
   * On Select some items from the check boxes
   * @param {*} { selected }
   * @memberof TableComponent
   */
  // onSelect({ selected }: any) {
  //   if (selected) {
  //     this.selected.splice(0, this.selected.length);
  //     this.selected.push(...selected);
  //   }
  // }

  /**
   * Navigate to Add Item Page
   * @returns {Promise<boolean>}
   * @memberof TableComponent
   */
  goToAddPage(): Promise<boolean> {
    return this.router.navigate(['../add'], { relativeTo: this.route.parent });
  }

  /**
   * Build the global search form
   * @memberof TableComponent
   */
  buildForm(): void {
    this.globalSearch = this.fb.group({
      search: ['']
    });
  }
}

// NOTES:
// - Flag to choose between server and client side operations {{ IMPORTANT }}
// - Fix the auto complete selector for fit the table cell
// - Add Option Input For Table Row Height
// - Sort With Server Side Pagination
